import React from 'react';
import { AppLayoutWrapper } from './AppLayoutWrapper';
import {
    Container,
    Header,
    SpaceBetween,
    Table,
    Box,
    Link,
} from '@cloudscape-design/components';

const ContentPolicyPage = () => {
    const definitionsItems = [
        {
            term: 'Content',
            definition:
                'Equipment service manuals, maintenance procedures, safety procedures and pre-requisites, hardware troubleshooting and recovery documentation, and safety certification materials hosted on GRU. All obligations with respect to Content apply to each item of Content, as well as to portions and derivations of that Content.',
        },
        {
            term: 'Content Consumer',
            definition:
                'A team, application, or system (including all its individuals and automated components) accessing GRU Content.',
        },
        {
            term: 'Content Author',
            definition:
                'The LExT (Learning Experience and Technology) Transformative Learning Solutions (TLS) team, responsible for authoring, reviewing, and approving Content in coordination with safety teams, following the TLS Style Guide.',
        },
        {
            term: 'Platform Owner',
            definition:
                'The ARGES team, responsible for the GRU platform — hosting, availability, and governance of access to Content.',
        },
        {
            term: 'Programmatic Access',
            definition:
                'Any access to GRU Content via API, web scraping, content ingestion, automated retrieval, or any mechanism other than direct human interaction with the GRU website.',
        },
        {
            term: 'Safety Pre-requisites',
            definition:
                'Required safety steps (including but not limited to FAST, UP, and Safe Access certifications) that must be completed before performing a procedure.',
        },
        {
            term: 'Active Session',
            definition:
                'A single, user-initiated, time-bounded interaction between an end user and a Content Consumer system, ending when the user terminates the interaction or after 24 hours, whichever comes first.',
        },
    ];

    const content = (
        <SpaceBetween size="l">
            <Container
                header={<Header variant="h1">GRU Content Usage Policy</Header>}
            >
                <SpaceBetween size="m">
                    <Box variant="h3">Summary</Box>
                    <Box>
                        This policy defines the acceptable use of content hosted
                        on Global Robotics University (GRU). It establishes
                        requirements for how GRU content may be accessed,
                        consumed, and presented — particularly in the context of
                        AI, agentic, or automated systems. These requirements
                        enable GRU content to be consumed in a safe, accurate,
                        and risk-mitigated manner.
                    </Box>
                </SpaceBetween>
            </Container>

            <Container header={<Header variant="h2">1. Definitions</Header>}>
                <Table
                    columnDefinitions={[
                        {
                            id: 'term',
                            header: 'Term',
                            cell: (item) => <strong>{item.term}</strong>,
                            width: 180,
                        },
                        {
                            id: 'definition',
                            header: 'Definition',
                            cell: (item) => item.definition,
                        },
                    ]}
                    items={definitionsItems}
                    variant="embedded"
                />
            </Container>

            <Container header={<Header variant="h2">2. Scope</Header>}>
                <SpaceBetween size="s">
                    <Box>
                        This policy applies to all GRU Content, including but
                        not limited to:
                    </Box>
                    <ul>
                        <li>Equipment-specific safety overview</li>
                        <li>Hardware overview</li>
                        <li>Preventative maintenance procedures</li>
                        <li>Replacement procedures</li>
                        <li>Troubleshooting procedures</li>
                        <li>Relevant software tools</li>
                    </ul>
                    <Box>
                        This policy applies to all Content Consumers, whether
                        accessing Content through the GRU website, an approved
                        integration, or any other channel.
                    </Box>
                </SpaceBetween>
            </Container>

            <Container
                header={<Header variant="h2">3. Content Classification</Header>}
            >
                <SpaceBetween size="s">
                    <Box>
                        <strong>3.1</strong> GRU Content enables safe and
                        efficient decision making by all who build, deploy,
                        maintain, and interact with AR technologies. Procedures
                        hosted on GRU are authored by the LExT TLS team (Content
                        Author) following the TLS Style Guide. The content is
                        reviewed and approved by Stakeholders and SME teams. The
                        GRU platform is owned and maintained by ARGES (Platform
                        Owner). These procedures define the required safety
                        steps for maintaining and recovering robotic equipment.
                    </Box>
                    <Box>
                        <strong>3.2</strong> GRU is the single source of truth
                        for FTR equipment maintenance procedures. Safety
                        statements within procedures are positionally structured
                        relative to specific steps (Danger {'>'} Warning {'>'}{' '}
                        Caution), and any reordering, summarization, or
                        separation of safety statements from their associated
                        steps creates risk of injury or equipment damage.
                    </Box>
                </SpaceBetween>
            </Container>

            <Container
                header={<Header variant="h2">4. Intended Audience</Header>}
            >
                <SpaceBetween size="s">
                    <Box>
                        <strong>4.1</strong> GRU Content is primarily intended
                        for Reliability Maintenance Engineering (RME)
                        technicians and other personnel who:
                    </Box>
                    <ul>
                        <li>
                            Are physically maintaining, troubleshooting, or
                            recovering faulted robotic equipment
                        </li>
                        <li>
                            Have completed required generalized workplace safety
                            training (e.g., FAST/UP certification)
                        </li>
                        <li>
                            Are authorized to perform the specific procedures
                            described in the Content
                        </li>
                    </ul>
                    <Box>
                        <strong>4.2</strong> GRU Content does not authorize any
                        individual to perform the procedures described within.
                        Authorization is determined by the individual's role,
                        training, and site-level certifications.
                    </Box>
                </SpaceBetween>
            </Container>

            <Container
                header={
                    <Header variant="h2">
                        5. Content Integrity Requirements
                    </Header>
                }
            >
                <SpaceBetween size="s">
                    <Box>
                        When GRU Content is presented to any end user — whether
                        through the GRU website, an approved integration, or any
                        other channel — the following requirements apply:
                    </Box>
                    <Box>
                        <strong>5.1 Verbatim delivery.</strong> Content Consumer
                        must present procedure content exactly as authored.
                        Summarization, paraphrasing, reinterpretation, or
                        selective omission of procedure steps is prohibited.
                        Content Consumer must not add commentary,
                        recommendations, annotations, or supplementary guidance
                        that could alter the interpretation of, contradict, or
                        encourage deviation from the procedure as authored.
                    </Box>
                    <Box>
                        <strong>5.2 Safety pre-requisites.</strong> Content
                        Consumer must present all Safety Pre-requisite
                        information associated with a procedure alongside the
                        procedure content. Safety information must not be
                        separated from, abbreviated, or omitted from the
                        procedure.
                    </Box>
                    <Box>
                        <strong>5.3 Source attribution.</strong> Content
                        Consumer must clearly attribute content to GRU with a
                        direct link to the source document on
                        learning.robotics.amazon.dev.
                    </Box>
                    <Box>
                        <strong>5.4 User qualification notice.</strong> Any
                        system presenting GRU Content must display the following
                        notice: "This procedure is sourced from Global Robotics
                        University (GRU). You must only perform procedures or
                        tasks appropriate for your role and for which you have
                        been properly trained or certified. This content does
                        not authorize you to perform the described procedure."
                    </Box>
                    <Box>
                        <strong>5.5 Structural preservation.</strong> Content
                        Consumer must preserve the ordering, hierarchy, and
                        format of procedure steps, including the positioning of
                        Danger, Warning, and Caution statements relative to
                        their associated steps. Content must not be converted
                        into alternative formats (e.g., flowcharts, decision
                        trees, conversational dialogue, or checklists) unless
                        the alternative format has been explicitly approved by
                        the Content Author.
                    </Box>
                    <Box>
                        <strong>5.6 Completeness.</strong> Content Consumer must
                        present procedures in their entirety. If a system is
                        unable to present a complete procedure due to technical
                        limitations (e.g., response length constraints), it must
                        clearly indicate that the procedure has been truncated
                        and direct the user to the full procedure on GRU.
                    </Box>
                </SpaceBetween>
            </Container>

            <Container
                header={
                    <Header variant="h2">6. Programmatic and AI Access</Header>
                }
            >
                <SpaceBetween size="m">
                    <Box variant="h3">6.1 Approved Access Only</Box>
                    <SpaceBetween size="s">
                        <Box>
                            <strong>6.1.1</strong> Programmatic Access to GRU
                            Content — including but not limited to API access,
                            web scraping, content ingestion for AI/LLM training,
                            or automated retrieval for use in external tools —
                            requires formal approval through Amazon Approvals.
                        </Box>
                        <Box>
                            <strong>6.1.2</strong> Content Consumer may only
                            access Content for which its use case has been
                            approved. Content approved for a specific use case
                            must not be used for any other purpose without
                            further approval. Material changes to the approved
                            system's architecture, user interface, content
                            presentation method, or end-user audience constitute
                            a new use case and require a new approval under
                            Section 6.2.
                        </Box>
                        <Box>
                            <strong>6.1.3</strong> All Content access requests
                            and grants must be recorded in Amazon Approvals for
                            audit purposes.
                        </Box>
                    </SpaceBetween>

                    <Box variant="h3">6.2 Approval Process</Box>
                    <SpaceBetween size="s">
                        <Box>
                            <strong>6.2.1</strong> The requesting team must
                            submit a GRU Content Access request via Amazon
                            Approvals. The request must include:
                        </Box>
                        <ul>
                            <li>
                                Business justification and use case description
                            </li>
                            <li>
                                End-user audience and their RME certification
                                status
                            </li>
                            <li>
                                Technical architecture showing how Content will
                                be retrieved, presented, and logged
                            </li>
                            <li>
                                Acknowledgment of all Content Integrity
                                Requirements (Section 5)
                            </li>
                            <li>
                                Confirmation that Content will not be used for
                                AI/ML model training or fine-tuning
                            </li>
                        </ul>
                        <Box>
                            <strong>6.2.2</strong> Requests route through the
                            following approval chain:
                        </Box>
                        <ul>
                            <li>
                                ARGES (Platform Owner) — Technical review of
                                integration approach and platform access
                                controls
                            </li>
                            <li>
                                Corporate Counsel — Legal review of use case and
                                risk assessment
                            </li>
                            <li>GRU Steering Committee — Final approval</li>
                        </ul>
                        <Box>
                            <strong>6.2.3</strong> Upon approval, the Content
                            Consumer must execute a Terms of Use agreement
                            before access is granted.
                        </Box>
                    </SpaceBetween>

                    <Box variant="h3">6.3 Conditions of Use</Box>
                    <SpaceBetween size="s">
                        <Box>
                            <strong>6.3.1</strong> Content Consumer must
                            restrict access to Content to individuals and
                            systems supporting the approved use case.
                        </Box>
                        <Box>
                            <strong>6.3.2</strong> Content Consumer must enforce
                            all Content Integrity Requirements defined in
                            Section 5.
                        </Box>
                        <Box>
                            <strong>6.3.3</strong> Content Consumer must provide
                            audit logging of Content access, including which
                            procedures were accessed, by whom, and when.
                        </Box>
                        <Box>
                            <strong>6.3.4</strong> Content Consumer must not use
                            GRU Content to train, fine-tune, or otherwise modify
                            AI/ML models. This includes, but is not limited to,
                            generating embeddings, populating vector databases,
                            building retrieval indices, or any other mechanism
                            that ingests GRU Content for use in AI-assisted
                            retrieval or generation — unless the specific
                            retrieval architecture has been reviewed and
                            approved under Section 6.2.
                        </Box>
                        <Box>
                            <strong>6.3.5</strong> Content Consumer must not
                            cache, store, or replicate GRU Content outside of
                            the consuming tool's active session, unless
                            explicitly approved in writing. An active session is
                            defined as a single, user-initiated, time-bounded
                            interaction; Content must be re-fetched from GRU for
                            each new session. Cached Content must be purged no
                            later than 24 hours after retrieval, regardless of
                            session state.
                        </Box>
                        <Box>
                            <strong>6.3.6</strong> Content Consumer must not
                            present GRU Content to end users who are not RME
                            technicians or equivalently trained/certified
                            personnel, as determined by the Content Author
                            (LExT) during the approval process, unless
                            explicitly approved. The Content Consumer may not
                            independently determine equivalency. Content
                            Consumer must implement reasonable technical
                            controls to verify end-user authorization (e.g.,
                            role-based access tied to RME certification records)
                            rather than relying solely on self-attestation.
                        </Box>
                        <Box>
                            <strong>6.3.7</strong> Content Consumer must
                            retrieve Content directly from GRU at the time of
                            presentation to the end user, or implement a
                            synchronization mechanism approved by the Platform
                            Owner that ensures Content is no more than 24 hours
                            out of date. Content Consumer must not serve Content
                            from a static snapshot or archive.
                        </Box>
                    </SpaceBetween>

                    <Box variant="h3">6.4 Rationale</Box>
                    <Box>
                        GRU Content is intended to specifically assist RME with
                        certain tasks, products, and procedures. Sharing the
                        Content more broadly creates risk of misuse or
                        misinterpretation, particularly when consumed by AI
                        systems that may provide inaccurate information or
                        recommendations. Individuals must not rely on
                        information pulled from GRU Content if it is not
                        relevant to their use case.
                    </Box>
                </SpaceBetween>
            </Container>

            <Container header={<Header variant="h2">7. Enforcement</Header>}>
                <SpaceBetween size="s">
                    <Box>
                        <strong>7.1</strong> The GRU website may implement
                        technical controls (including but not limited to
                        robots.txt, API authentication, and rate limiting) to
                        prevent unauthorized Programmatic Access.
                    </Box>
                    <Box>
                        <strong>7.2</strong> Approved integrations are subject
                        to periodic review. The Platform Owner reserves the
                        right to audit any Content Consumer's compliance with
                        this policy at any time.
                    </Box>
                    <Box>
                        <strong>7.3</strong> The Platform Owner reserves the
                        right to suspend or revoke access to any Content
                        Consumer that does not comply with this policy, without
                        prior notice if the non-compliance poses a safety risk.
                    </Box>
                </SpaceBetween>
            </Container>

            <Container header={<Header variant="h2">8. Violations</Header>}>
                <SpaceBetween size="s">
                    <Box>
                        <strong>8.1</strong> Unauthorized Programmatic Access to
                        GRU Content, or use of Content in violation of the
                        Conditions of Use (Section 6.3), constitutes a breach of
                        this policy.
                    </Box>
                    <Box>
                        <strong>8.2</strong> Violations must be reported to the
                        Platform Owner (sysops-arges@amazon.com) or the GRU
                        Steering Committee.
                    </Box>
                    <Box>
                        <strong>8.3</strong> Breach of this policy may result in
                        immediate revocation of access and escalation to the
                        violating team's management chain.
                    </Box>
                    <Box>
                        <strong>8.4</strong> Content Consumer must promptly
                        notify the Platform Owner (sysops-arges@amazon.com) upon
                        discovering that its system has served Content
                        incorrectly — including but not limited to truncated
                        procedures, reordered steps, stale content, or content
                        presented to unauthorized users — even if the
                        non-compliance was unintentional.
                    </Box>
                </SpaceBetween>
            </Container>

            <Container
                header={
                    <Header variant="h2">9. Post-Access Termination</Header>
                }
            >
                <SpaceBetween size="s">
                    <Box>
                        <strong>9.1</strong> Upon revocation or expiration of
                        approved access, the Content Consumer must immediately
                        cease all Programmatic Access to GRU Content.
                    </Box>
                    <Box>
                        <strong>9.2</strong> Content Consumer must delete any
                        cached, stored, or replicated GRU Content within 5
                        business days of access termination.
                    </Box>
                    <Box>
                        <strong>9.3</strong> Content Consumer must confirm
                        deletion in writing to the Platform Owner.
                    </Box>
                </SpaceBetween>
            </Container>

            <Container header={<Header variant="h2">10. Exceptions</Header>}>
                <SpaceBetween size="s">
                    <Box>
                        <strong>10.1</strong> All exceptions to this policy must
                        be approved by the Platform Owner (ARGES), the Content
                        Author (LExT), and Corporate Counsel.
                    </Box>
                    <Box>
                        <strong>10.2</strong> Exception requests must be
                        submitted to sysops-arges@amazon.com with a written
                        justification, the specific policy clause(s) for which
                        an exception is requested, and the proposed duration of
                        the exception.
                    </Box>
                    <Box>
                        <strong>10.3</strong> Approved exceptions must be
                        documented and are subject to periodic review.
                    </Box>
                </SpaceBetween>
            </Container>

            <Container
                header={<Header variant="h2">11. Related Policies</Header>}
            >
                <ul>
                    <li>Amazon Acceptable Use Policy</li>
                    <li>Data Classification and Handling Policy</li>
                    <li>
                        Third Party Generative AI Use and Interaction Policy
                    </li>
                    <li>Generative AI Security Standard</li>
                    <li>AGI Content Access Policy (structural reference)</li>
                    <li>AGI Content Handling Policy (structural reference)</li>
                    <li>
                        Creators Data Sharing Policy (data sharing governance
                        reference)
                    </li>
                    <li>
                        GenAI Data Handling Guide (agentic system security
                        guidance)
                    </li>
                    <li>LExT TLS Style Guide (content authoring standards)</li>
                </ul>
            </Container>

            <Container header={<Header variant="h2">12. Contact</Header>}>
                <SpaceBetween size="s">
                    <Box>
                        <strong>Access requests:</strong> Submit via Amazon
                        Approvals using the GRU Content Access template
                    </Box>
                    <Box>
                        <strong>Platform (ARGES):</strong>{' '}
                        <Link href="mailto:sysops-arges@amazon.com">
                            sysops-arges@amazon.com
                        </Link>
                    </Box>
                    <Box>
                        <strong>Content (LExT):</strong> Submit via LExT RIVER
                        workflow
                    </Box>
                </SpaceBetween>
            </Container>
        </SpaceBetween>
    );

    return <AppLayoutWrapper content={content} />;
};

export default ContentPolicyPage;
